import React, { useCallback, useMemo, useState } from "react";
import { Maximize2 } from "lucide-react";
import { usePortfolio } from "../../contexts/PortfolioContext";
import Lightbox from "../public/Lightbox";
import SectionHeading from "../public/SectionHeading";

const Gallery = () => {
  const [selected, setSelected] = useState(null);
  const { portfolioData } = usePortfolio();

  const achievements = useMemo(
    () =>
      (portfolioData.achievements || [])
        .filter((item) => item.image)
        .map((item) => ({
          title: item.title,
          caption: item.issuer,
          date: item.date,
          image: item.image,
          description: item.description || "",
          type: "Certificate",
        }))
        .reverse(),
    [portfolioData.achievements]
  );

  const activities = useMemo(
    () =>
      (portfolioData.activities || [])
        .filter((item) => item.image)
        .map((item) => ({
          title: item.title,
          caption: item.role || item.period,
          date: item.period,
          image: item.image,
          description: item.description || "",
          type: "Activity",
        }))
        .reverse(),
    [portfolioData.activities]
  );

  const closeLightbox = useCallback(() => setSelected(null), []);

  if (achievements.length === 0 && activities.length === 0) return null;

  return (
    <section className="public-section public-section--white" id="gallery">
      <div className="container">
        <SectionHeading
          eyebrow="Highlights"
          title="Gallery"
          description="Certificates, activities, and moments from the journey so far."
          aside={
            <span className="section-count">
              {achievements.length + activities.length} items
            </span>
          }
        />

        {achievements.length > 0 && (
          <div className="gallery-group">
            <div className="gallery-group-head">
              <h3>Awards & certificates</h3>
              <div className="divider" />
              <span className="gallery-group-count">
                {achievements.length}{" "}
                {achievements.length === 1 ? "item" : "items"}
              </span>
            </div>

            <div className="row gallery-grid">
              {achievements.map((item, index) => (
                <div className="col s12 m6 l4" key={`award-${index}`}>
                  <GalleryCard
                    item={item}
                    certificate
                    onOpen={() => setSelected(item)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {activities.length > 0 && (
          <div className="gallery-group">
            <div className="gallery-group-head">
              <h3>Activities</h3>
              <div className="divider" />
              <span className="gallery-group-count">
                {activities.length}{" "}
                {activities.length === 1 ? "item" : "items"}
              </span>
            </div>

            <div className="row gallery-grid">
              {activities.map((item, index) => (
                <div className="col s12 m6 l4" key={`activity-${index}`}>
                  <GalleryCard
                    item={item}
                    onOpen={() => setSelected(item)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Lightbox
        open={Boolean(selected)}
        onClose={closeLightbox}
        label={selected?.title || "Gallery preview"}
      >
        {selected && (
          <>
            <div className="lightbox-head">
              <span className="lightbox-title">{selected.title}</span>
              <span className="lightbox-count">{selected.type}</span>
            </div>
            <div className="lightbox-image-wrap">
              <img src={selected.image} alt={selected.title} />
            </div>
            {(selected.caption || selected.date || selected.description) && (
              <p className="lightbox-caption">
                {[selected.caption, selected.date]
                  .filter(Boolean)
                  .join(" · ")}
                {selected.description ? ` — ${selected.description}` : ""}
              </p>
            )}
          </>
        )}
      </Lightbox>
    </section>
  );
};

const GalleryCard = ({ item, certificate = false, onOpen }) => (
  <article
    className={`gallery-card${
      certificate ? " gallery-card--certificate" : ""
    }`}
    role="button"
    tabIndex={0}
    aria-label={`View ${item.title}`}
    onClick={onOpen}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen();
      }
    }}
  >
    <div className="gallery-media">
      <img src={item.image} alt={item.title} loading="lazy" decoding="async" />
      <span className="gallery-zoom" aria-hidden="true">
        <Maximize2 size={16} />
      </span>
    </div>
    <div className="gallery-card-body">
      <span className="gallery-type">{item.type}</span>
      <h4 className="gallery-title">{item.title}</h4>
      {item.caption && <p className="gallery-caption">{item.caption}</p>}
    </div>
  </article>
);

export default Gallery;
